import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useMinimalRecentEntryAvailability } from 'features/menu/pages/recent-items-page/minimal-recent-items/use-minimal-recent-entry-availability';
import { useStoreContext } from 'features/store';
import { View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import { IErrorModal } from 'hooks/use-error-modal';
import useErrorModal from 'hooks/use-error-modal';
import { logAddRecentItemToCartEvent } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { routes } from 'utils/routing';

import { MinimalOrderAction } from './minimal-order-action';
import { OrderPointsEarned } from './order-points-earned';
import { RewardText } from './reward-text';
import {
  Col,
  FormattedDate,
  ItemNameContainer,
  Items,
  MoreItems,
  MoreItemsText,
  NumItems,
  OrderBox,
  OrderNumber,
  OrderNumberText,
  OrderNumberWrapper,
  SubtitleContainer,
  Tile,
} from './styled';
import { MinimalAccountOrder } from './types';

type MinimalOrderErrorModal = Pick<IErrorModal, 'message' | 'modalAppearanceEventMessage'>;

const MinimalOrder = ({ order }: { order: MinimalAccountOrder }) => {
  const { store, fetchStore, isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const { availability, hasSomeAvailableEntry } = useMinimalRecentEntryAvailability(
    order.minimalEntries
  );
  const router = useRouter();
  const [isReordering, setIsReordering] = useState(false);

  const [ErrorDialog, openErrorDialog] = useErrorModal<MinimalOrderErrorModal>({
    modalAppearanceEventMessage: 'Error: Fetch Store Failure',
  });

  const formattedDate = useMemo(
    () =>
      new Date(order.createdAt).toLocaleString(t('localeDateTime'), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [order.createdAt]
  );

  const { itemCSV, numItems } = useMemo(() => {
    const csvForItem = order.minimalEntries.map(entry => entry.name).join(', ');
    const totalItemsOrdered = order.minimalEntries.reduce((acc, item) => acc + item.quantity, 0);

    const itemsCount = `${totalItemsOrdered} ${(totalItemsOrdered === 1
      ? t('item')
      : t('items')
    ).toLowerCase()}`;

    return { itemCSV: csvForItem, numItems: itemsCount };
  }, [order.minimalEntries]);

  const showOrderModal = useCallback(() => {
    router.navigate(routes.orderDetails(order.rbiOrderId));
  }, [order.rbiOrderId, router]);

  const handleFinishOrder = async () => {
    const orderStoreId = order.cart?.storeId ? order.cart.storeId.toString() : '';

    if (store.number && store.number !== orderStoreId) {
      try {
        await fetchStore(orderStoreId);
      } catch {
        return openErrorDialog({ message: t('couldNotLocateStore') });
      }
    }

    router.replace(`${routes.orderConfirmation}/${order.rbiOrderId}`);
  };

  const handleReorder = useCallback(() => {
    if (!isStoreOpenAndAvailable || noStoreSelected) {
      return;
    }

    const availableEntries = order.minimalEntries.filter(
      (_, index) => availability[index]?.itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE
    );

    setIsReordering(true);

    availableEntries.forEach(entry => {
      logAddRecentItemToCartEvent(entry?.name ?? '', store);
      addMappedEntryToCart(entry);
    });

    const [firstEntry] = availableEntries;
    if (firstEntry) {
      const message =
        availableEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    router.replace(routes.cart);
  }, [
    addMappedEntryToCart,
    availability,
    isStoreOpenAndAvailable,
    noStoreSelected,
    order.minimalEntries,
    router,
    store,
  ]);

  const itemsArr = useMemo(() => (itemCSV ? itemCSV.split(',') : []), [itemCSV]);
  const remainingItems = Math.max(itemsArr.length - 1, 0);
  const renderMoreItems = useCallback(
    () => (
      <MoreItems onPress={() => showOrderModal()}>
        <MoreItemsText>{t('moreItems', { remainingItems })}</MoreItemsText>
      </MoreItems>
    ),
    [remainingItems, showOrderModal]
  );
  const formattedItemsList = useMemo(
    () =>
      itemsArr.length > 0 ? [itemsArr[0], ...(remainingItems > 0 ? [renderMoreItems()] : [])] : [],
    [itemsArr, remainingItems, renderMoreItems]
  );

  const disabled = !hasSomeAvailableEntry || !isStoreOpenAndAvailable || noStoreSelected;
  const showTrackOrderModalPage = () =>
    router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`);

  return (
    <>
      <Tile testID={`recent-order-${order.rbiOrderId}`}>
        <Col focusable>
          <OrderNumberWrapper>
            {order.orderNumberFormatted && (
              <>
                <OrderNumberText variant="headerTwo">{`${t('orderNumber')}:`}</OrderNumberText>
                <OrderNumber variant="headerTwo">{order.orderNumberFormatted}</OrderNumber>
              </>
            )}
          </OrderNumberWrapper>
          <FormattedDate variant="headerTwo">{formattedDate}</FormattedDate>
          <SubtitleContainer variant="headerThree">
            <View style={{ flexDirection: 'row' }}>
              <NumItems>{numItems}</NumItems>
              {order.loyaltyTransaction && (
                <OrderPointsEarned
                  loyaltyTransaction={{
                    id: order.loyaltyTransaction.id,
                    pointsEarned: order.loyaltyTransaction.pointsEarned,
                    bonusPointsEarned: order.loyaltyTransaction.bonusPointsEarned,
                    pointsUsed: 0,
                    pointsBalance: 0,
                    rewardsUsed: 0,
                  }}
                />
              )}
            </View>
          </SubtitleContainer>
          <ItemNameContainer>
            {order.hasLoyaltySelection && <RewardText text={t('rewards')} />}
            <Items>{formattedItemsList}</Items>
          </ItemNameContainer>
        </Col>
        <Col>
          <Box width="full">
            <ActionButton
              testID="recent-order-view-details"
              variant={ActionButtonVariants.OUTLINE}
              fullWidth
              onPress={showOrderModal}
              aria-label={
                order.orderNumberFormatted
                  ? t('viewDetailsWithOrderNumber', { ticketNumber: order.orderNumberFormatted })
                  : t('viewDetails')
              }
            >
              {t('viewDetails')}
            </ActionButton>
            <OrderBox>
              <MinimalOrderAction
                order={order}
                loading={isReordering}
                disabled={disabled}
                onTrack={showTrackOrderModalPage}
                onReorder={handleReorder}
                onFinish={handleFinishOrder}
              />
            </OrderBox>
          </Box>
        </Col>

        <ErrorDialog />
      </Tile>
    </>
  );
};

export default MinimalOrder;
